import { defaultDataType } from 'export-xlsx';
import { exportDataExcel } from '@/api/util';

export function billToSummaryData(data) {
	if (!Array.isArray(data)) return [];

	const validReceiptDateList = getValidReceiptDateList(data);

	const receiptDateList = [];
	let index = 1;
	for (const date of validReceiptDateList) {
		const receiptDate = {
			valid_receipt_date: date,
			receipt_type_list: [],
			total_amount: 0,
		};

		const receiptTypeList = [];
		for (const type of data) {
			const receiptType = {
				receipt_type_code: type.receipt_type_code || 'Unknown',
				receipt_type_name: type.receipt_type_name || 'Unknown',
				receipt_item_list: [],
				total_amount: 0,
			};

			for (const receipt of type.receiptTypeDateList) {
				if (receipt.valid_receipt_date === date) {
					for (const bill of receipt.billList) {
						let bill_to_receipt_item_list = [];
						for (const item of bill.receiptItemList) {
							if (bill_to_receipt_item_list.find(ri => ri.receipt_item_no === item.receipt_item_no)) continue;
							const receiptItem = {
								index: index++,
								receipt_item_no: item.receipt_item_no,
								receipt_item_amount: item.receipt_item_amount,
								check_no: item.check_no,
								post_check_yn: item.post_check_yn,
								post_check_date: item.post_check_date,
								bounce_yn: item.bounce_yn,
								bounce_date: item.bounce_date,
								bounce_invoice_no: item.bounce_invoice_no,
								receipt_type: type.receipt_type_code,
								valid_receipt_date: receipt.valid_receipt_date,
								bill_to_code: bill.bill_to_code,
								bill_to_name: bill.bill_to_name,
							};
							bill_to_receipt_item_list.push(receiptItem);
						}
						receiptType.receipt_item_list.push(...bill_to_receipt_item_list);
					}
				}
			}
			if (receiptType.receipt_item_list.length === 0) continue;
			receiptType.total_amount = receiptType.receipt_item_list.reduce((acc, cur) => acc + cur.receipt_item_amount, 0);
			receiptTypeList.push(receiptType);
		}
		receiptDate.receipt_type_list = receiptTypeList;
		receiptDate.total_amount = receiptTypeList.reduce((acc, cur) => acc + cur.total_amount, 0);
		receiptDateList.push(receiptDate);
	}

	receiptDateList.sort((a, b) => {
		// MM-dd-yyyy 형식의 문자열을 날짜 객체로 변환하는 함수
		const parseDate = dateStr => {
			const [month, day, year] = dateStr.split('-');
			return new Date(year, month - 1, day); // month는 0부터 시작하므로 -1
		};

		const dateA = parseDate(a.valid_receipt_date);
		const dateB = parseDate(b.valid_receipt_date);

		return dateA - dateB;
	});

	const totalAmount = receiptDateList.reduce((acc, cur) => acc + cur.total_amount, 0);
	return { bill_to_summary_list: receiptDateList, bill_to_summary_total_amount: totalAmount };
}

function getValidReceiptDateList(data) {
	return new Set(
		data.flatMap(item => {
			if (item.receiptTypeDateList) {
				return item.receiptTypeDateList.map(dateItem => dateItem.valid_receipt_date);
			}
			return [];
		}),
	);
}

export function exportReceiptTypeDataExcel(data, fileName) {
	let headers = [
		{ key: 'index', name: 'No.', width: 15, dataType: defaultDataType.currency },
		{ key: 'receipt_type_code', name: 'Receipt Type', width: 20, dataType: defaultDataType.string },
		{ key: 'valid_receipt_date', name: 'Valid Receipt Date', width: 15, dataType: defaultDataType.string },
		{ key: 'sales_group_name', name: 'Sales Group', width: 15, dataType: defaultDataType.string },
		{ key: 'bill_to_code', name: 'Bill To', width: 15, dataType: defaultDataType.string },
		{ key: 'bill_to_name', name: 'Bill To Name', width: 25, dataType: defaultDataType.string },
		{ key: 'bill_address_state', name: 'STATE', width: 10, dataType: defaultDataType.string },
		{ key: 'receipt_item_amount', name: 'Receipt Amt.', width: 15, dataType: defaultDataType.number },
		{ key: 'invoice_dn', name: 'Billing No.', width: 15, dataType: defaultDataType.string },
		{ key: 'invoice_amount', name: 'Invoice Amt.', width: 15, dataType: defaultDataType.number },
		{ key: 'ar_amount', name: 'AR Amount', width: 15, dataType: defaultDataType.number },
		{ key: 'check_no', name: 'Check No.', width: 15, dataType: defaultDataType.string },
		{ key: 'payment_term_code', name: 'Payment Term', width: 15, dataType: defaultDataType.string },
		{ key: 'past_due_date', name: 'Past Due Date', width: 15, dataType: defaultDataType.currency },
		{ key: 'receipt_remark', name: 'Receipt Remark', width: 30, dataType: defaultDataType.string },
	];

	exportDataExcel(data, headers, fileName);
}

export function exportReceiptTypeSummaryDataExcel(data, fileName) {
	let headers = [
		{ key: 'valid_receipt_date', name: 'Valid Receipt Date', width: 15, dataType: defaultDataType.string },
		{ key: 'receipt_type_code', name: 'Receipt Type', width: 20, dataType: defaultDataType.string },
		{ key: 'bill_to_code', name: 'Bill To', width: 15, dataType: defaultDataType.string },
		{ key: 'bill_to_name', name: 'Bill To Name', width: 25, dataType: defaultDataType.string },
		{ key: 'receipt_item_amount', name: 'Receipt Amt.', width: 15, dataType: defaultDataType.number },
		{ key: 'check_no', name: 'Check No.', width: 15, dataType: defaultDataType.string },
	];

	let excelData = [];

	for (const date of data) {
		excelData.push({ valid_receipt_date: date.valid_receipt_date, receipt_type_code: '', index: '', bill_to_code: '', bill_to_name: '', receipt_item_amount: '', check_no: '' });
		for (const type of date.receipt_type_list) {
			excelData.push({ valid_receipt_date: '', receipt_type_code: `${type.receipt_type_code} ${type.receipt_type_name}`, index: '', bill_to_code: '', bill_to_name: '', receipt_item_amount: '', check_no: '' });
			for (const item of type.receipt_item_list) {
				excelData.push({ ...item, valid_receipt_date: '', receipt_type_code: '' });
			}
			excelData.push({ valid_receipt_date: '', receipt_type_code: `Sub Total`, index: '', bill_to_code: '', bill_to_name: '', receipt_item_amount: type.total_amount, check_no: '' });
		}
		excelData.push({ valid_receipt_date: 'Sub Total', receipt_type_code: '', index: '', bill_to_code: '', bill_to_name: '', receipt_item_amount: date.total_amount, check_no: '' });
	}
	let totalAmount = data.reduce((acc, cur) => acc + cur.total_amount, 0);
	excelData.push({ valid_receipt_date: 'Grand Total', receipt_item_amount: totalAmount, check_no: '' });

	exportDataExcel(excelData, headers, fileName);
}

export function exportReceiptTypeDateSummaryDataExcel(data, fileName) {
	let headers = [
		{ key: 'type', name: 'Receipt Type', width: 20, dataType: defaultDataType.string },
		{ key: 'amount', name: 'Amount', width: 15, dataType: defaultDataType.number },
	];

	exportDataExcel(data, headers, fileName);
}
